import { useState, useCallback } from "react";
import { cn } from "../../lib/utils";
import type { LaneId, Owner, Priority } from "../../data/types";

interface NewTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (fields: {
    laneId: string;
    title: string;
    description: string;
    status: "planned" | "in_progress";
    owner: Owner;
    priority: Priority;
    tags: string[];
    notes: string;
  }) => void;
}

const LANES: Array<{ id: LaneId; label: string }> = [
  { id: "objective_kpis", label: "Objectives & KPIs" },
  { id: "offer_pricing", label: "Offer & Pricing" },
  { id: "channels", label: "Channels" },
  { id: "commercial_pipeline", label: "Commercial Pipeline" },
  { id: "weekly_content", label: "Weekly Content" },
  { id: "revops_tracking", label: "RevOps & Tracking" },
  { id: "feedback_loop", label: "Feedback Loop" },
];

const TASK_TYPES = ["one_off", "recurring_daily", "recurring_weekly"] as const;

export function NewTaskDialog({ open, onOpenChange, onCreate }: NewTaskDialogProps) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [laneId, setLaneId] = useState<LaneId>("channels");
  const [owner, setOwner] = useState<Owner>("Joint");
  const [priority, setPriority] = useState<Priority>("medium");
  const [taskType, setTaskType] = useState<typeof TASK_TYPES[number]>("one_off");
  const [notes, setNotes] = useState("");

  const reset = useCallback(() => {
    setTitle("");
    setDescription("");
    setLaneId("channels");
    setOwner("Joint");
    setPriority("medium");
    setTaskType("one_off");
    setNotes("");
  }, []);

  const handleSubmit = useCallback(() => {
    if (!title.trim()) return;
    onCreate({
      laneId,
      title: title.trim(),
      description: description.trim(),
      status: "planned",
      owner,
      priority,
      tags: [`task:${taskType}`, "phase:p1"],
      notes: notes.trim(),
    });
    reset();
    onOpenChange(false);
  }, [title, description, laneId, owner, priority, taskType, notes, onCreate, reset, onOpenChange]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-black/50 animate-fade-in"
        onClick={() => onOpenChange(false)}
        role="presentation"
      />
      <div className="absolute left-1/2 top-[15%] -translate-x-1/2 w-full max-w-[480px] animate-stagger-in">
        <div
          className={cn(
            "rounded-[var(--radius-lg)] border border-[var(--color-border-stroke)]",
            "bg-[var(--color-bg-surface)] shadow-[var(--shadow-modal)] overflow-hidden",
          )}
        >
          <div className="px-[var(--spacing-4)] py-[var(--spacing-3)] border-b border-[var(--color-border-stroke)]">
            <h2 className="text-[14px] font-semibold text-[var(--color-text-primary)]">New Task</h2>
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
            onKeyDown={(e) => { if (e.key === "Escape") onOpenChange(false); }}
            className="p-[var(--spacing-4)] space-y-[var(--spacing-3)]"
          >
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--color-text-muted)] mb-[var(--spacing-1)]">
                Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Task title…"
                autoFocus
                className={cn(
                  "w-full h-[36px] rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
                  "bg-[var(--color-bg-canvas)] px-[var(--spacing-3)]",
                  "text-[14px] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)]",
                  "focus-visible:outline-none focus-visible:border-[var(--color-accent-focus)]",
                )}
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--color-text-muted)] mb-[var(--spacing-1)]">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description…"
                rows={2}
                className={cn(
                  "w-full rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
                  "bg-[var(--color-bg-canvas)] px-[var(--spacing-3)] py-[var(--spacing-2)]",
                  "text-[14px] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)]",
                  "focus-visible:outline-none focus-visible:border-[var(--color-accent-focus)] resize-y",
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-[var(--spacing-3)]">
              <SelectField
                label="Lane"
                value={laneId}
                onChange={(v) => setLaneId(v as LaneId)}
                options={LANES.map((l) => ({ value: l.id, label: l.label }))}
              />
              <SelectField
                label="Owner"
                value={owner}
                onChange={(v) => setOwner(v as Owner)}
                options={[
                  { value: "Joel", label: "Joel" },
                  { value: "Albert", label: "Albert" },
                  { value: "Joint", label: "Joint" },
                  { value: "AI Wave", label: "AI Wave" },
                ]}
              />
              <SelectField
                label="Priority"
                value={priority}
                onChange={(v) => setPriority(v as Priority)}
                options={[
                  { value: "critical", label: "Critical" },
                  { value: "high", label: "High" },
                  { value: "medium", label: "Medium" },
                  { value: "low", label: "Low" },
                ]}
              />
              <SelectField
                label="Task Type"
                value={taskType}
                onChange={(v) => setTaskType(v as typeof TASK_TYPES[number])}
                options={TASK_TYPES.map((t) => ({ value: t, label: t.replace(/_/g, " ") }))}
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--color-text-muted)] mb-[var(--spacing-1)]">
                Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="window_cet=09:00-10:00 | sla=2h | segment=waitlist"
                rows={2}
                className={cn(
                  "w-full rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
                  "bg-[var(--color-bg-canvas)] px-[var(--spacing-3)] py-[var(--spacing-2)]",
                  "text-[14px] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)]",
                  "focus-visible:outline-none focus-visible:border-[var(--color-accent-focus)] resize-y font-mono",
                )}
              />
            </div>

            <div className="flex justify-end gap-[var(--spacing-2)] pt-[var(--spacing-2)]">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className={cn(
                  "h-[36px] px-[var(--spacing-4)] rounded-[var(--radius-md)]",
                  "text-[14px] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]",
                  "transition-colors cursor-pointer",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
                )}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!title.trim()}
                className={cn(
                  "h-[36px] px-[var(--spacing-4)] rounded-[var(--radius-md)]",
                  "bg-[var(--color-accent-500)] text-white text-[14px] font-medium",
                  "hover:bg-[var(--color-accent-400)] active:scale-[0.98]",
                  "disabled:opacity-40 disabled:cursor-not-allowed",
                  "transition-all cursor-pointer",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
                )}
              >
                Create Task
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function SelectField({ label, value, onChange, options }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--color-text-muted)] mb-[var(--spacing-1)]">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "w-full h-[36px] rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
          "bg-[var(--color-bg-canvas)] px-[var(--spacing-2)]",
          "text-[14px] text-[var(--color-text-primary)] appearance-none cursor-pointer",
          "focus-visible:outline-none focus-visible:border-[var(--color-accent-focus)]",
        )}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}
